import React, { useState } from 'react'
import { Link } from 'gatsby'
import { StaticImage } from 'gatsby-plugin-image'
import { CallToAction } from 'components/CallToAction'
import { prepareData } from 'utils/googleData'
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image'
import { useTranslatedProps } from 'utils/translateProps'
import { useTranslation } from 'react-i18next'
// TODO: Remove this import before production
import sampleData from './sample.json'

interface PresenterProps {
    name: string
    role: string
    image: string
    link?: string
    description: string
}

interface CTAProps {
    title: string
    type: 'primary' | 'secondary'
    url: string
    openQuickContact?: boolean
}

type BooleanOrString = boolean | string
type VideoType = 'wistia' | 'youtube'

interface DemoVideoProps {
    enable?: BooleanOrString
    title?: string
    subtitle?: string
    description?: string
    videoId: string
    videoType?: VideoType
    showPresenter?: BooleanOrString
    presenter?: PresenterProps
    cta?: CTAProps
    cta2?: CTAProps
    className?: string
    direction?: 'ltr' | 'rtl' | 'auto'
    introductionText?: string
    gatsbyImage?: IGatsbyImageData
    showHeader?: BooleanOrString
    form?: any
    showVideoCTA?: BooleanOrString
    videoCTA?: CTAProps
}

export const DemoVideo: React.FC<DemoVideoProps> = (props) => {
    const testProps = process.env.NODE_ENV === 'development' ? sampleData : props
    const [isVideoHovered, setIsVideoHovered] = useState(false)

    const {
        enable,
        title,
        subtitle,
        description,
        videoId,
        videoType = 'wistia',
        showPresenter,
        showHeader = false,
        presenter,
        cta,
        cta2,
        className,
        direction,
        gatsbyImage,
        introductionText = 'Introducing our',
        form,
        showVideoCTA = false,
        videoCTA,
    } = prepareData({ props: testProps }) as DemoVideoProps
    const { i18n } = useTranslation()
    const translatedProps = useTranslatedProps(testProps, i18n.language)?.allDemoVideo

    const isEnabled = typeof enable === 'string' ? enable.toLowerCase() !== 'false' : enable !== false
    const shouldShowPresenter =
        typeof showPresenter === 'string' ? showPresenter.toLowerCase() !== 'false' : showPresenter !== false
    const shouldShowHeader =
        typeof showHeader === 'string' ? showHeader.toLowerCase() !== 'false' : showHeader !== false
    const shouldShowVideoCTA =
        typeof showVideoCTA === 'string' ? showVideoCTA.toLowerCase() !== 'false' : showVideoCTA !== false

    if (!isEnabled && !process.env.RENDER_DISABLED_COMPONENTS) {
        return null
    }

    const getVideoSrc = () => {
        switch (videoType) {
            case 'youtube':
                return `https://www.youtube.com/embed/${videoId}?rel=0`
            case 'wistia':
            default:
                return `https://fast.wistia.net/embed/iframe/${videoId}?videoFoam=true`
        }
    }

    return (
        <div dir={direction} className={`space-y-4 ${className}`}>
            {shouldShowHeader && (
                <header className="flex flex-col justify-between pb-4 md:flex-row md:items-center">
                    <div className="max-w-2xl">
                        {title && <h1 className="mt-0 mb-2 text-4xl font-bold tracking-tight">{title}</h1>}
                        {subtitle && <h2 className="text-gray-600 mt-0 mb-2 text-2xl">{subtitle}</h2>}
                        {description && <p className="text-gray-500 p-0 text-lg md:m-0">{description}</p>}
                    </div>
                </header>
            )}

            {shouldShowPresenter && presenter && (
                <div
                    className="mb-4 flex flex-col space-y-4 rounded-lg border border-dashed border-gray-accent-light bg-white p-4 shadow-sm transition-all hover:shadow-md dark:bg-gray-accent-dark sm:flex-row sm:space-x-4 sm:space-y-0"
                    dir={direction}
                >
                    <div className="flex items-center space-x-4">
                        <span className="flex h-12 w-12 shrink-0 basis-12 overflow-hidden rounded-full bg-blue leading-none ring-2 ring-blue/20">
                            {gatsbyImage && (
                                <GatsbyImage
                                    image={gatsbyImage}
                                    alt={presenter.name}
                                    className="h-full w-full"
                                    imgStyle={{ objectFit: `contain` }}
                                />
                            )}
                        </span>

                        <p className="text-gray-600 dark:text-gray-100 p-2 text-[20px]">
                            {presenter.role && `${introductionText} ${translatedProps.presenter.role}, `}
                            {presenter.link ? (
                                <Link
                                    to={presenter.link}
                                    className="dark:text-blue-300 font-semibold text-red transition-colors hover:text-red"
                                >
                                    {translatedProps.presenter.name}
                                </Link>
                            ) : (
                                presenter.name
                            )}
                            {presenter.description && ` ${translatedProps.presenter.description}`}
                        </p>
                    </div>

                    <div className="flex w-full flex-col gap-2 sm:ml-auto sm:w-auto sm:flex-row sm:items-center">
                        {cta && (
                            <CallToAction
                                type={cta.type}
                                to={cta.url}
                                className="hover:scale-105 w-full whitespace-nowrap transition-transform sm:w-auto"
                                form={form}
                                openQuickContact={cta.openQuickContact}
                            >
                                {cta.title}
                            </CallToAction>
                        )}
                        {cta2 && (
                            <CallToAction
                                type={cta2.type}
                                to={cta2.url}
                                className="hover:scale-105 w-full whitespace-nowrap transition-transform sm:w-auto"
                                form={form}
                                openQuickContact={cta2.openQuickContact}
                            >
                                {cta2.title}
                            </CallToAction>
                        )}
                    </div>
                </div>
            )}

            <div
                id="video-section"
                className="group relative mb-4 space-x-4 rounded-lg border border-dashed border-gray-accent-light bg-white p-4 shadow-sm transition-all hover:shadow-md"
                onMouseEnter={() => setIsVideoHovered(true)}
                onMouseLeave={() => setIsVideoHovered(false)}
            >
                {shouldShowVideoCTA && videoCTA && (
                    <div className="mb-4 text-center">
                        <CallToAction
                            type={videoCTA.type}
                            to={videoCTA.url}
                            className="hover:scale-105 whitespace-nowrap transition-transform"
                            form={form}
                            openQuickContact={videoCTA.openQuickContact}
                        >
                            {translatedProps.videoCTA.title}
                        </CallToAction>
                    </div>
                )}
                <div className="wistia_responsive_padding">
                    <div className="wistia_responsive_wrapper">
                        <iframe
                            src={getVideoSrc()}
                            className={`rounded-lg shadow-xl transition-all duration-300 ${
                                isVideoHovered ? 'scale-[1.02]' : 'scale-100'
                            }`}
                            title="Demo Video"
                            allow="autoplay; fullscreen"
                            allowFullScreen
                            frameBorder="0"
                            scrolling="no"
                            width="100%"
                            height="100%"
                        />
                    </div>
                </div>
                <div className="pointer-events-none absolute inset-0 rounded-lg bg-gradient-to-t from-black/5 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
            </div>
        </div>
    )
}
